import {
  type PatientBlacklistInput,
  type PatientsExportParams,
  type PatientsListParams,
  type PatientsWaSummaryParams,
  type UpdatePatientInput,
  api,
} from '@/lib/api-client'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

/**
 * Hook to fetch paginated patients list
 */
export function usePatients(params?: PatientsListParams) {
  return useQuery({
    queryKey: ['patients', params],
    queryFn: () => api.patients.list(params),
    placeholderData: keepPreviousData, // Smooth pagination transitions
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Hook to fetch single patient with stats
 */
export function usePatient(id: number | null) {
  return useQuery({
    queryKey: ['patient', id],
    queryFn: () => api.patients.get(id!),
    enabled: id !== null && id > 0,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook to fetch patient invoices
 */
export function usePatientInvoices(
  id: number | null,
  params?: { page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: ['patient', id, 'invoices', params],
    queryFn: () => api.patients.invoices(id!, params),
    enabled: id !== null && id > 0,
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook to update patient
 */
export function useUpdatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePatientInput }) =>
      api.patients.update(id, data),

    onSuccess: (response, variables) => {
      // Invalidate patients list
      queryClient.invalidateQueries({ queryKey: ['patients'] })

      // Invalidate specific patient
      queryClient.invalidateQueries({ queryKey: ['patient', variables.id] })

      toast.success('Patient updated successfully')
    },

    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update patient')
    },
  })
}

/**
 * Blacklist / un-blacklist a patient (clinic-side "do not contact").
 * Also refreshes follow-up queries — the open episode is closed server-side.
 */
export function useSetPatientBlacklist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PatientBlacklistInput }) =>
      api.patients.setBlacklist(id, data),

    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['patient', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] })
      toast.success(
        variables.data.blacklisted ? 'Patient blacklisted' : 'Patient removed from blacklist'
      )
    },

    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update blacklist')
    },
  })
}

/**
 * Hook to fetch patients for export
 */
export function usePatientsExport(params?: PatientsExportParams, enabled = false) {
  return useQuery({
    queryKey: ['patients', 'export', params],
    queryFn: () => api.patients.export(params),
    enabled,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Pool sizes per WhatsApp campaign-history filter (for the filter dropdown)
 */
export function usePatientsWaSummary(params?: PatientsWaSummaryParams) {
  return useQuery({
    queryKey: ['patients', 'wa-summary', params],
    queryFn: () => api.patients.waSummary(params),
    staleTime: 5 * 60 * 1000,
  })
}
